import { useState, useRef, useEffect, KeyboardEvent, ClipboardEvent } from 'react';
import { Box, TextField, Typography } from '@mui/material';

interface BarcodeInputProps {
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  error?: boolean;
  helperText?: string;
  label?: string;
  disabled?: boolean;
}

/**
 * BarcodeInput Component
 * 
 * Accepts barcode format: R[2-9]{1}\d{6}M\d{4}
 * Example: R2123456M7890
 * 
 * - Fixed letters (R, M) act as separators
 * - Only digits are editable
 * - Single-digit inputs with underline styling
 */
export function BarcodeInput({
  value,
  onChange,
  onComplete,
  error = false,
  helperText,
  label = 'الباركود',
  disabled = false,
}: BarcodeInputProps) {
  // Parse barcode into individual digits
  // Format: R + 1 digit (2-9) + 6 digits + M + 4 digits = 13 characters total
  const parseBarcode = (barcode: string): string[] => {
    const clean = barcode.replace(/[^0-9]/g, ''); // Remove non-digits
    const digits = clean.split('').slice(0, 11); // Max 11 digits
    return [...digits, ...Array(11 - digits.length).fill('')];
  };

  const [digits, setDigits] = useState<string[]>(() => parseBarcode(value));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Update parent when digits change
  useEffect(() => {
    const digitString = digits.join('');
    let newValue = digitString;

    if (digitString.length >= 11) {
      const firstDigit = digits[0];
      const middleDigits = digits.slice(1, 7).join('');
      const lastDigits = digits.slice(7, 11).join('');
      newValue = `R${firstDigit}${middleDigits}M${lastDigits}`;
    }

    // Only call onChange if the value actually changed to prevent loops
    if (newValue !== value) {
      onChange(newValue);
      
      if (digitString.length === 11 && onComplete && newValue.length === 13) {
        onComplete(newValue);
      }
    }
  }, [digits]); // Remove onChange, onComplete, and value from dependencies

  // Update digits when value prop changes externally
  useEffect(() => {
    const newDigits = parseBarcode(value);
    // Only update if digits actually changed
    const currentString = digits.join('');
    const newString = newDigits.join('');
    if (currentString !== newString) {
      setDigits(newDigits);
    }
  }, [value]); // Only depend on value

  const handleChange = (index: number, newValue: string) => {
    // Only allow single digit
    const digit = newValue.replace(/[^0-9]/g, '').slice(-1);

    // Validate first digit (must be 2-9)
    if (index === 0 && digit && (parseInt(digit) < 2 || parseInt(digit) > 9)) {
      return;
    }

    const newDigits = [...digits];
    newDigits[index] = digit;
    setDigits(newDigits);

    // Auto-focus next input
    if (digit && index < 10) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      // Move to previous input on backspace if current is empty
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 10) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const pastedDigits = pastedData.replace(/[^0-9]/g, '').split('').slice(0, 11);

    // Validate first digit
    if (pastedDigits[0] && (parseInt(pastedDigits[0]) < 2 || parseInt(pastedDigits[0]) > 9)) {
      return;
    }

    const newDigits = [...pastedDigits, ...Array(11 - pastedDigits.length).fill('')];
    setDigits(newDigits);

    // Focus last filled input
    const lastFilledIndex = pastedDigits.length - 1;
    if (lastFilledIndex >= 0 && lastFilledIndex < 11) {
      inputRefs.current[lastFilledIndex]?.focus();
    }
  };

  return (
    <Box>
      {label && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {label}
        </Typography>
      )}

      <div
        style={{
          display: 'flex',
          gap: '2px',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          direction: 'ltr',
          justifyContent: 'flex-start',
          maxWidth: '100%',
        }}
        onPaste={handlePaste}
        dir="ltr"
      >
        {/* First group: R + 7 digits (stays together, won't wrap internally) */}
        <div
          style={{
            display: 'flex',
            gap: '2px',
            alignItems: 'center',
            flexWrap: 'nowrap',
            flexShrink: 0,
          }}
        >
          {/* R prefix */}
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: 'text.secondary',
              minWidth: 20,
              textAlign: 'center',
            }}
          >
            R
          </Typography>

          {/* First digit (2-9) */}
          <TextField
            inputRef={(el) => (inputRefs.current[0] = el)}
            value={digits[0]}
            onChange={(e) => handleChange(0, e.target.value)}
            onKeyDown={(e) => handleKeyDown(0, e)}
            disabled={disabled}
            error={error}
            inputProps={{
              maxLength: 1,
              inputMode: 'numeric',
              dir: 'ltr',
              style: {
                textAlign: 'center',
                fontSize: '1rem',
                fontWeight: 500,
                padding: '6px 0',
                direction: 'ltr',
              },
            }}
            InputProps={{
              sx: {
                direction: 'ltr !important',
                '& input': {
                  direction: 'ltr !important',
                },
              },
            }}
            sx={{
              width: 32,
              direction: 'ltr !important',
              '& .MuiOutlinedInput-root': {
                direction: 'ltr !important',
                '& fieldset': {
                  border: 'none',
                  borderBottom: error ? '2px solid' : '2px solid',
                  borderBottomColor: error ? 'error.main' : 'divider',
                  borderRadius: 0,
                },
                '&:hover fieldset': {
                  borderBottomColor: error ? 'error.main' : 'primary.main',
                },
                '&.Mui-focused fieldset': {
                  borderBottomColor: error ? 'error.main' : 'primary.main',
                  borderBottomWidth: 2,
                },
              },
            }}
          />

          {/* Middle 6 digits */}
          {digits.slice(1, 7).map((digit, idx) => (
            <TextField
              key={`mid-${idx}`}
              inputRef={(el) => (inputRefs.current[idx + 1] = el)}
              value={digit}
              onChange={(e) => handleChange(idx + 1, e.target.value)}
              onKeyDown={(e) => handleKeyDown(idx + 1, e)}
              disabled={disabled}
              error={error}
              inputProps={{
                maxLength: 1,
                inputMode: 'numeric',
                dir: 'ltr',
                style: {
                  textAlign: 'center',
                  fontSize: '1rem',
                  fontWeight: 500,
                  padding: '6px 0',
                  direction: 'ltr',
                },
              }}
              InputProps={{
                sx: {
                  direction: 'ltr !important',
                  '& input': {
                    direction: 'ltr !important',
                  },
                },
              }}
              sx={{
                width: 32,
                direction: 'ltr !important',
                '& .MuiOutlinedInput-root': {
                  direction: 'ltr !important',
                  '& fieldset': {
                    border: 'none',
                    borderBottom: '2px solid',
                    borderBottomColor: error ? 'error.main' : 'divider',
                    borderRadius: 0,
                  },
                  '&:hover fieldset': {
                    borderBottomColor: error ? 'error.main' : 'primary.main',
                  },
                  '&.Mui-focused fieldset': {
                    borderBottomColor: error ? 'error.main' : 'primary.main',
                    borderBottomWidth: 2,
                  },
                },
              }}
            />
          ))}
        </div>

        {/* Second group: M + 4 digits (can wrap to new line) */}
        <div
          style={{
            display: 'flex',
            gap: '2px',
            alignItems: 'center',
            flexWrap: 'nowrap',
            flexShrink: 0,
          }}
        >
          {/* M separator */}
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: 'text.secondary',
              minWidth: 20,
              textAlign: 'center',
            }}
          >
            M
          </Typography>

          {/* Last 4 digits */}
          {digits.slice(7, 11).map((digit, idx) => (
            <TextField
              key={`last-${idx}`}
              inputRef={(el) => (inputRefs.current[idx + 7] = el)}
              value={digit}
              onChange={(e) => handleChange(idx + 7, e.target.value)}
              onKeyDown={(e) => handleKeyDown(idx + 7, e)}
              disabled={disabled}
              error={error}
              inputProps={{
                maxLength: 1,
                inputMode: 'numeric',
                dir: 'ltr',
                style: {
                  textAlign: 'center',
                  fontSize: '1rem',
                  fontWeight: 500,
                  padding: '6px 0',
                  direction: 'ltr',
                },
              }}
              InputProps={{
                sx: {
                  direction: 'ltr !important',
                  '& input': {
                    direction: 'ltr !important',
                  },
                },
              }}
              sx={{
                width: 32,
                direction: 'ltr !important',
                '& .MuiOutlinedInput-root': {
                  direction: 'ltr !important',
                  '& fieldset': {
                    border: 'none',
                    borderBottom: '2px solid',
                    borderBottomColor: error ? 'error.main' : 'divider',
                    borderRadius: 0,
                  },
                  '&:hover fieldset': {
                    borderBottomColor: error ? 'error.main' : 'primary.main',
                  },
                  '&.Mui-focused fieldset': {
                    borderBottomColor: error ? 'error.main' : 'primary.main',
                    borderBottomWidth: 2,
                  },
                },
              }}
            />
          ))}
        </div>
      </div>

      {helperText && (
        <Typography
          variant="caption"
          color={error ? 'error' : 'text.secondary'}
          sx={{ mt: 0.5, display: 'block' }}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
}
